import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { DeployMicroserviceRequest } from "@microtica/ms-kube-sdk";
import { Button, Card, Col, Collapse, Divider, Form, Input, notification, Row, Space } from "antd";
import { RequiredMark } from "antd/lib/form/Form";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { getKubeService } from "../../backend";
import ExplanationButton from "../../components/explanations/ExplanationButton";
import AppSettingsScalingExplanation from "../../components/explanations/AppSettingsScalingExplanation";
import { IAppDetails } from "../../types";

const AppSettingsScaling = () => {
    const [scalingForm] = Form.useForm();
    const { projectId, clusterId, appName } = useParams();
    const { appDetails: { image, configuration, namespace, monitoring } } = useOutletContext<{ appDetails: IAppDetails }>();
    const [requiredMark] = useState<RequiredMark>('optional');

    const [initialCpu, setInitialCpu] = useState(monitoring.initialCpu);
    const [cpuLimit, setCpuLimit] = useState(monitoring.cpuLimit);
    const [initialMemory, setInitialMemory] = useState(monitoring.initialMemory);
    const [memoryLimit, setMemoryLimit] = useState(monitoring.memoryLimit);
    const [minReplicas, setMinReplicas] = useState(monitoring.minReplicas);
    const [maxReplicas, setMaxReplicas] = useState(monitoring.maxReplicas);
    const [loadingScaling, setLoadingScaling] = useState(false);

    const deployApplication = async () => {
        const microserviceRequest: DeployMicroserviceRequest = {
            deployment: {
                image,
                configurations: configuration,
                containerPort: parseInt(configuration?.find(c => c.key === "MIC_CONTAINER_PORT")?.value || "80")
            },
            autoScaling: {
                cpu: initialCpu,
                maxCpu: cpuLimit,
                memory: initialMemory,
                maxMemory: memoryLimit,
                cpuUtilization: 80,
                memoryUtilization: 80,
                minReplicas: minReplicas,
                maxReplicas: maxReplicas
            }
        }

        await getKubeService().deployMicroservice(
            appName!,
            clusterId!,
            namespace,
            projectId!,
            microserviceRequest
        )
    }

    const handleConfigureScaling = async () => {
        try {
            setLoadingScaling(true);

            await deployApplication();

            notification.success({
                message: "Scaling initiated",
                description: "It would take a few moments to apply the changes."
            });
        } catch (error: any) {
            notification.error({
                message: "Application scaling failed",
                description: error.response.data.message
            });
        } finally {
            setLoadingScaling(false);
        }
    }

    return (
        <Space direction="vertical" className="full-width">
            <Card bordered>
                <Card.Meta
                    title={
                        <>
                            <span>
                                Scaling
                            </span>
                            <ExplanationButton
                                content={<AppSettingsScalingExplanation />}
                            />
                        </>
                    }
                    description="Limit the resources that application should use and configure the number of instances."
                />
                <Divider />

                <Space direction="vertical" size="large">
                    Automated scaling will be initiated when the application reaches 80% of the maximum assigned CPU or Memory resources, within the maximum number of instances.
                    <Form
                        form={scalingForm}
                        layout="vertical"
                        requiredMark={requiredMark}
                        initialValues={{
                            cpu: initialCpu,
                            maxCpu: cpuLimit,
                            memory: initialMemory,
                            maxMemory: memoryLimit,
                            minReplicas: minReplicas,
                            maxReplicas: maxReplicas
                        }}
                        onFinish={handleConfigureScaling}
                    >
                        <Collapse defaultActiveKey="cpu">
                            <Collapse.Panel key="cpu" header="CPU">
                                <Row gutter={[24, 12]}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="cpu"
                                            label="Initial"
                                            required
                                            tooltip="The initial CPU resources given to the application"
                                            rules={[{ required: true, message: `Please input the minimum CPU resources!` }]}
                                        >
                                            <Input type="number" addonAfter="mCPU" value={initialCpu} onChange={(e) => setInitialCpu(parseInt(e.target.value))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="maxCpu"
                                            label="Initial"
                                            required
                                            tooltip="The maximum allowed CPU resources given to the application"
                                            rules={[{ required: true, message: `Please input the maxumim CPU resources!` }]}
                                        >
                                            <Input type="number" addonAfter="mCPU" value={cpuLimit} onChange={(e) => setCpuLimit(parseInt(e.target.value))} />
                                        </Form.Item>
                                    </Col>

                                    <Col>
                                        {
                                            monitoring.cpuUsage === "NaNm" ?
                                                <><LoadingOutlined /> Collecting usage data:</> :
                                                <><InfoCircleOutlined /> The application is currently using <b>{monitoring.cpuUsage} vCPU</b>.</>
                                        }
                                    </Col>
                                </Row>
                            </Collapse.Panel>
                            <Collapse.Panel key="memory" header="Memory">
                                <Row gutter={[24, 12]}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="memory"
                                            label="Minumum"
                                            required
                                            tooltip="The initial memory given to the application"
                                            rules={[{ required: true, message: `Please input the minimum memory!` }]}
                                        >
                                            <Input type="number" addonAfter="MiB" value={initialMemory} onChange={(e) => setInitialMemory(parseInt(e.target.value))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="maxMemory"
                                            label="Maximum"
                                            required
                                            tooltip="The maximum allowed memory given to the application"
                                            rules={[{ required: true, message: `Please input the maxumim memory!` }]}
                                        >
                                            <Input type="number" addonAfter="MiB" value={memoryLimit} onChange={(e) => setMemoryLimit(parseInt(e.target.value))} />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        {
                                            monitoring.memoryUsage === "NaNMi" ?
                                                <><LoadingOutlined /> Collecting usage data</> :
                                                <><InfoCircleOutlined /> The application is currently using <b>{monitoring.memoryUsage}B</b>.</>
                                        }
                                    </Col>
                                </Row>
                            </Collapse.Panel>
                            <Collapse.Panel key="instances" header="Replication">
                                <Row gutter={[24, 12]}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="minReplicas"
                                            label="Minimum"
                                            required
                                            tooltip="The minimum number of running instances of the application"
                                            rules={[
                                                { required: true, message: "Please input the minimum instances!" },
                                                {
                                                    validator: async (_, value) => {
                                                        if (value < 1) {
                                                            throw new Error("Should be at least 1");
                                                        }
                                                        if (value > maxReplicas) {
                                                            throw new Error(`Should be lower or equal to maximum instances`);
                                                        }
                                                    }
                                                }
                                            ]}
                                        >
                                            <Input type="number" addonAfter="instances" value={minReplicas} onChange={(e) => setMinReplicas(parseInt(e.target.value))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="maxReplicas"
                                            label="Maximum"
                                            required
                                            tooltip="The maximum number of running instances of the application"
                                            rules={[
                                                { required: true, message: `Please input the maxumim instances!` },
                                                {
                                                    validator: async (_, value) => {
                                                        if (value < 1) {
                                                            throw new Error("Should be at least 1");
                                                        }
                                                        if (value < minReplicas) {
                                                            throw new Error(`Should be higher or equal to minimum instances`);
                                                        }
                                                    }
                                                }
                                            ]}
                                        >
                                            <Input type="number" addonAfter="instances" value={maxReplicas} onChange={(e) => setMaxReplicas(parseInt(e.target.value))} />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <InfoCircleOutlined /> The application is currently running on <b>{monitoring.runningPods}</b> instance(s).
                                    </Col>
                                </Row>
                            </Collapse.Panel>
                        </Collapse>
                        <br />

                        <Row style={{ display: "flex", alignItems: "center" }}>
                            <Col span={12}>
                                Learn more about <a href="https://docs.microtica.com/">Application Scaling</a>
                            </Col>
                            <Col span={12} style={{ textAlign: "right" }}>
                                <Button htmlType="submit" loading={loadingScaling}>
                                    Save
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Space>
            </Card>
        </Space>
    );
}

export default AppSettingsScaling;